/**
 * The one ordering rule for every CRM queue list — Clean, Match and Send.
 *
 * The backend emits rows in UNSPECIFIED order: neither `selectPersonsForConference`
 * nor `selectPersonsForExport` carries an `ORDER BY`, and the latter's
 * `id = ANY($3)` does not preserve the request array either. Both engines then
 * index-align their outputs to that input. So the wire order is not "insertion
 * order" — it is whatever the executor produced, and it genuinely reshuffles
 * between two refreshes (heap order after an UPDATE, after a VACUUM, on a plan
 * change). Sorting here is therefore not cosmetic: it is what makes these lists
 * REPRODUCIBLE for the operator working through them.
 *
 * `displayName` is the only name on the wire (`PersonMatchState` / `PersonPreview`
 * in ./types carry no first/last split) and the only thing every row renders, so
 * it is what "alphabetized" means on these screens.
 */

/**
 * Module-scope singleton on purpose: `String.prototype.localeCompare` with an
 * options object constructs a fresh Collator per call, which dominates an
 * O(n log n) sort of a full conference roster.
 *
 * `sensitivity: 'base'` — case- and accent-insensitive, matching the shared
 * table comparator (`packages/ui/src/table/columnDefaults.ts`).
 * `numeric: true` — "Lead 2" before "Lead 10".
 */
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/** The minimum shape both CRM wire row types satisfy structurally. */
export interface NameSortable {
  personId: string;
  displayName: string;
}

/** personIds are ASCII ids — a relational compare, not the collator, is the right tool. */
function byPersonId(a: NameSortable, b: NameSortable): number {
  if (a.personId < b.personId) return -1;
  if (a.personId > b.personId) return 1;
  return 0;
}

/**
 * A–Z by the visible label, blanks LAST, ties broken by `personId`.
 *
 * Blanks last, not first: `persons.display_name` is NOT NULL but is routinely
 * the empty string — the mobile app writes one while a card capture is in flight
 * or when OCR found no name — and a run of nameless rows at the TOP of an A–Z
 * list reads as a broken list. Same "nullish sorts after present" rule the
 * shared table comparator uses. Placeholder names ('Unnamed lead',
 * 'Photo lead — 14:32') are real strings, not blanks: they interleave under U
 * and P, which is what the operator sees on the row.
 *
 * The `personId` tiebreak is load-bearing rather than decoration: Array#sort is
 * stable, but the INPUT order is not, so two identically-named people would
 * still swap places between refetches without it.
 */
export function byDisplayName(a: NameSortable, b: NameSortable): number {
  const an = a.displayName.trim();
  const bn = b.displayName.trim();
  if (an === '' || bn === '') {
    if (an !== bn) return an === '' ? 1 : -1;
    return byPersonId(a, b);
  }
  const byName = collator.compare(an, bn);
  return byName !== 0 ? byName : byPersonId(a, b);
}

/** Non-mutating convenience — never sort a props array in place. */
export function sortedByName<T extends NameSortable>(rows: readonly T[]): T[] {
  return [...rows].sort(byDisplayName);
}
